// @flow

import {debounceTime, map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {ActionsObservable, ofType, StateObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import type {RootState} from '..';
import {
  createSubcategoryApi,
  editSubcategoryApi,
  deleteSubcategoryApi,
  endpoints,
  sortSubcategoryApi,
  getSubcategoriesApi,
} from './api';

import type {Subcategory, Pagination} from '../../entities';
import {buildRoute} from '../../http';

export const SIZE = 16;
export type Movement = {
  init?: {pos: number, id: string},
  end?: {pos: number, id: string},
};

export type SubcategoryRequest = {
  id?: string,
  categoryId?: string,
  name?: string,
  movement?: Movement,
  subcategory?: Subcategory,
};

export type CreateSubCategoryItemRequest = {
  subcategoryId: string,
  name: string,
};

export type SortSubCategoryItemRequest = {
  originProductSubCategoryId: string,
  targetProductSubCategoryId: string,
};

export type EditSubCategoryRequest = {
  name: string,
  subcategoryItemId: string,
};

export type SubcategoryAction = Action & {
  subcategoryRequest?: SubcategoryRequest,
  categoryId?: string,
  subcategory?: Subcategory,
  subcategories?: Subcategory[],
  pagination?: Pagination,
};

export type SubcategoryState = {
  isSaving?: boolean,
  pagination?: Pagination,
  isFetchingSubcategories?: boolean,
  subcategories?: Subcategory[],
  subcategory?: Subcategory,
  createSubcategorySuccess?: boolean,
  updateSubcategorySuccess?: boolean,
  deleteSubcategorySuccess?: boolean,
  sortSubcategorySuccess?: boolean,
  createSubcategoryError?: string,
  updateSubcategoryError?: string,
  deleteSubcategoryError?: string,
  sortSubcategoryError?: string,
};

export class FetchSubcategories extends ActionCreator<SubcategoryAction> {}
export class FetchSubcategoriesSuccess extends ActionCreator<SubcategoryAction> {}
export class FetchSubcategoriesError extends ActionCreator<SubcategoryAction> {}

export class CreateSubcategory extends ActionCreator<SubcategoryAction> {}
export class CreateSubcategorySuccess extends ActionCreator<SubcategoryAction> {}
export class CreateSubcategoryError extends ActionCreator<SubcategoryAction> {}

export class EditSubcategory extends ActionCreator<SubcategoryAction> {}
export class EditSubcategorySuccess extends ActionCreator<SubcategoryAction> {}
export class EditSubcategoryError extends ActionCreator<SubcategoryAction> {}

export class DeleteSubcategory extends ActionCreator<SubcategoryAction> {}
export class DeleteSubcategorySuccess extends ActionCreator<SubcategoryAction> {}
export class DeleteSubcategoryError extends ActionCreator<SubcategoryAction> {}

export class SortSubcategory extends ActionCreator<SubcategoryAction> {}
export class SortSubcategorySuccess extends ActionCreator<SubcategoryAction> {}
export class SortSubcategoryError extends ActionCreator<SubcategoryAction> {}

export class ResetState extends ActionCreator<SubcategoryAction> {}
export class ResetSubcategories extends ActionCreator<SubcategoryAction> {}

export class CreateSubcategoryItem extends ActionCreator<CreateSubCategoryItemRequest> {}
export class SortSubcategoryItem extends ActionCreator<SortSubCategoryItemRequest> {}
export class EditSubcategoryItem extends ActionCreator<EditSubCategoryRequest> {}

export const actions = {
  fetchSubcategories: (payload: SubcategoryAction) =>
    new FetchSubcategories(payload),
  editSubcategory: (payload: SubcategoryAction) => new EditSubcategory(payload),
  deleteSubcategory: (payload: SubcategoryAction) =>
    new DeleteSubcategory(payload),
  createSubcategory: (payload: SubcategoryAction) =>
    new CreateSubcategory(payload),
  sortSubcategory: (payload: SubcategoryAction) => new SortSubcategory(payload),
  resetSubcategoriesState: () => new ResetState(),
  resetSubcategoriesFilter: () => new ResetSubcategories(),

  createSubCategoryItem: (subcategoryId: string, name: string) =>
    new CreateSubcategory(),
};

export const initialState = {};

export default createReducer(initialState, {
  [FetchSubcategories.type]: (state: SubcategoryState) => ({
    ...state,
    isFetchingSubcategories: true,
  }),
  [FetchSubcategoriesSuccess.type]: (
    state: SubcategoryState,
    action: SubcategoryAction
  ) => ({
    ...state,
    isFetchingSubcategories: undefined,
    subcategories: action.subcategories,
    pagination: action.pagination,
  }),
  [FetchSubcategoriesError.type]: (state: SubcategoryState) => ({
    ...state,
    isFetchingSubcategories: undefined,
  }),
  [EditSubcategory.type]: (state: SubcategoryState) => ({
    ...state,
    isSaving: true,
    createSubcategorySuccess: undefined,
    createSubcategoryError: undefined,
    deleteSubcategorySuccess: undefined,
    deleteSubcategoryError: undefined,
    updateSubcategorySuccess: undefined,
    updateSubcategoryError: undefined,
  }),
  [EditSubcategorySuccess.type]: (state: SubcategoryState) => ({
    ...state,
    isSaving: undefined,
    updateSubcategorySuccess: true,
    updateSubcategoryError: undefined,
  }),
  [EditSubcategoryError.type]: (
    state: SubcategoryState,
    action: SubcategoryAction
  ) => ({
    ...state,
    isSaving: undefined,
    updateSubcategorySuccess: undefined,
    updateSubcategoryError: action.error,
  }),
  [CreateSubcategory.type]: (state: SubcategoryState) => ({
    ...state,
    isSaving: true,
    subcategory: undefined,
    createSubcategorySuccess: undefined,
    createSubcategoryError: undefined,
    deleteSubcategorySuccess: undefined,
    deleteSubcategoryError: undefined,
    updateSubcategorySuccess: undefined,
    updateSubcategoryError: undefined,
  }),
  [CreateSubcategorySuccess.type]: (
    state: SubcategoryState,
    action: SubcategoryAction
  ) => {
    const subcategory = {categoryId: action.categoryId, ...action.subcategory};
    return {
      ...state,
      subcategory,
      isSaving: undefined,
      createSubcategorySuccess: true,
      createSubcategoryError: undefined,
    };
  },
  [CreateSubcategoryError.type]: (
    state: SubcategoryState,
    action: SubcategoryAction
  ) => ({
    ...state,
    isSaving: undefined,
    createSubcategorySuccess: undefined,
    createSubcategoryError: action.error,
  }),
  [DeleteSubcategory.type]: (state: SubcategoryState) => ({
    ...state,
    isSaving: true,
    createSubcategorySuccess: undefined,
    createSubcategoryError: undefined,
    deleteSubcategorySuccess: undefined,
    deleteSubcategoryError: undefined,
    updateSubcategorySuccess: undefined,
    updateSubcategoryError: undefined,
  }),
  [DeleteSubcategorySuccess.type]: (state: SubcategoryState) => ({
    ...state,
    isSaving: undefined,
    deleteSubcategorySuccess: true,
    deleteSubcategoryError: undefined,
  }),
  [DeleteSubcategoryError.type]: (
    state: SubcategoryState,
    action: SubcategoryAction
  ) => ({
    ...state,
    isSaving: undefined,
    deleteSubcategorySuccess: undefined,
    deleteSubcategoryError: action.error,
  }),
  [SortSubcategory.type]: (state: SubcategoryState) => ({
    ...state,
    isSaving: true,
    sortSubcategorySuccess: undefined,
    sortSubcategoryError: undefined,
  }),
  [SortSubcategorySuccess.type]: (state: SubcategoryState) => ({
    ...state,
    isSaving: undefined,
    sortSubcategorySuccess: true,
    sortSubcategoryError: undefined,
  }),
  [SortSubcategoryError.type]: (
    state: SubcategoryState,
    action: SubcategoryAction
  ) => ({
    ...state,
    isSaving: undefined,
    sortSubcategorySuccess: undefined,
    sortSubcategoryError: action.error,
  }),
  [ResetState.type]: () => initialState,
  [ResetSubcategories.type]: (state: SubcategoryState) => ({
    ...state,
    subCategories: undefined,
  }),
});

export function fetchSubcategoriesEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchSubcategories.type),
    debounceTime(500),
    switchMap(({queryFilter, addPage}: SubcategoryAction) => {
      const pagination = state$.value.subCategories.pagination;

      const qf =
        pagination && addPage
          ? {...queryFilter, page: pagination.page + 1, size: SIZE}
          : {...queryFilter, page: 0, size: SIZE};

      return getSubcategoriesApi(qf).pipe(
        map(
          ({data, pagination}: {data: Subcategory[], pagination: Pagination}) =>
            new FetchSubcategoriesSuccess({subcategories: data, pagination})
        ),
        buildCatchError(FetchSubcategoriesError)
      );
    })
  );
}

export function updateSubcategoryEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditSubcategory.type),
    switchMap(({subcategoryRequest, id}: SubcategoryAction) => {
      const sbRequest = {name: subcategoryRequest.name};
      return editSubcategoryApi(sbRequest, {
        route: buildRoute(endpoints.EDIT_SUB_CATEGORY, [id]),
      }).pipe(
        map((subcategory: Subcategory) => new EditSubcategorySuccess()),
        buildCatchError(EditSubcategoryError)
      );
    })
  );
}

export function createSubcategoryEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CreateSubcategory.type),
    switchMap(({subcategoryRequest}: SubcategoryAction) => {
      const categoryId = subcategoryRequest.category;
      const sbRequest = {name: subcategoryRequest.name};
      return createSubcategoryApi(sbRequest, {
        route: buildRoute(endpoints.CREATE_SUB_CATEGORY, [categoryId]),
      }).pipe(
        map(
          (subcategory: Subcategory) =>
            new CreateSubcategorySuccess({categoryId, subcategory})
        ),
        buildCatchError(CreateSubcategoryError, 'Error al crear subcategoría')
      );
    })
  );
}

export function deleteSubcategoryEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DeleteSubcategory.type),
    switchMap(({subcategoryRequest}: SubcategoryAction) => {
      return deleteSubcategoryApi({
        route: buildRoute(endpoints.DELETE_SUB_CATEGORY, [
          subcategoryRequest.id,
        ]),
      }).pipe(
        map((subcategory: Subcategory) => new DeleteSubcategorySuccess()),
        buildCatchError(
          DeleteSubcategoryError,
          'Error al eliminar subcategoría'
        )
      );
    })
  );
}

export function sortSubcategoryEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SortSubcategory.type),
    switchMap(({subcategoryRequest}: SubcategoryAction) => {
      const {movement} = subcategoryRequest;
      const sortRequest = {
        originProductSubCategoryId:
          movement && movement.init ? movement.init.id : '',
        targetProductSubCategoryId:
          movement && movement.end ? movement.end.id : '',
      };
      return sortSubcategoryApi(sortRequest, {
        route: buildRoute(endpoints.SORT_SUB_CATEGORY, [
          subcategoryRequest.categoryId,
        ]),
      }).pipe(
        map((subcategory: Subcategory) => new SortSubcategorySuccess()),
        buildCatchError(SortSubcategoryError, 'Error al ordenar subcategoría')
      );
    })
  );
}
